/**
 * Real-time Events Context
 * 
 * Provides a single persistent WebSocket connection for the entire application.
 * The connection stays alive across page navigation and only disconnects on logout.
 */

import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { clearAuthToken } from '../data/api/HttpClient';
import { refreshToken } from '../data/api/authApi';

// Event types matching backend schema
export type EventType =
  | 'router_online'
  | 'router_offline'
  | 'router_metrics'
  | 'user_connected'
  | 'user_disconnected'
  | 'session_started'
  | 'session_ended'
  | 'session_paused'
  | 'session_resumed'
  | 'plan_purchased'
  | 'voucher_redeemed'
  | 'coin_inserted'
  | 'high_temperature'
  | 'high_cpu_usage'
  | 'high_memory_usage'
  | 'activity_list'
  | 'recent_routers_list'
  | 'recent_online_users_list';

// Base event interface
export interface BaseEvent {
  eventType: EventType;
  routerId: string;
  routerName?: string;
  location?: string;
  agentId?: string;
  timestamp: string;
}

// Re-export all event types from useRealTimeEvents
export type RealTimeEvent = any; // Will match all event types

// Connection state
export type ConnectionState = 'connecting' | 'connected' | 'disconnected' | 'error';

interface RealTimeEventsContextType {
  events: RealTimeEvent[];
  connectionState: ConnectionState;
  isConnected: boolean;
  subscribe: (callback: (event: RealTimeEvent) => void) => () => void;
  clearEvents: () => void;
  reconnect: () => void;
}

const RealTimeEventsContext = createContext<RealTimeEventsContextType | undefined>(undefined);

/**
 * Get WebSocket URL based on environment
 */
function getWebSocketUrl(): string {
  if (import.meta.env.VITE_WS_URL) {
    return import.meta.env.VITE_WS_URL;
  }

  const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api/v1';
  
  if (apiUrl.startsWith('/')) {
    const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    return `${wsProtocol}://${window.location.host}/ws`;
  }
  
  const wsProtocol = apiUrl.startsWith('https') ? 'wss' : 'ws';
  const baseUrl = apiUrl.replace(/^https?:\/\//, '').replace(/\/api\/v1$/, '');
  return `${wsProtocol}://${baseUrl}/ws`;
}

interface RealTimeEventsProviderProps {
  children: React.ReactNode;
  maxEvents?: number;
  autoReconnect?: boolean;
  reconnectDelay?: number;
  maxReconnectAttempts?: number;
}

export function RealTimeEventsProvider({
  children,
  maxEvents = 100,
  autoReconnect = true,
  reconnectDelay = 3000,
  maxReconnectAttempts = 10,
}: RealTimeEventsProviderProps) {
  const { user } = useAuth();
  const [events, setEvents] = useState<RealTimeEvent[]>([]);
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const subscribersRef = useRef<Set<(event: RealTimeEvent) => void>>(new Set());
  const wasConnectedRef = useRef(false);
  const isRefreshingTokenRef = useRef(false);

  // Add event to state and notify subscribers
  const addEvent = useCallback((event: RealTimeEvent) => {
    setEvents((prev) => {
      const newEvents = [event, ...prev];
      return newEvents.slice(0, maxEvents);
    });

    // Notify all subscribers
    subscribersRef.current.forEach(callback => {
      try {
        callback(event);
      } catch (error) {
        console.error('[RealTime] Subscriber callback error:', error);
      }
    });
  }, [maxEvents]);

  /**
   * Handle session expiry: clear auth and redirect to login.
   * Called when we detect the token is invalid/expired and cannot be refreshed.
   */
  const handleSessionExpired = useCallback(() => {
    console.warn('[RealTime] Session expired, redirecting to login');
    clearAuthToken();
    if (!window.location.pathname.includes('/login')) {
      window.location.href = '/login';
    }
  }, []);

  /**
   * Check if the auth token still exists in localStorage.
   * It may have been cleared by an HTTP 401 from another request.
   */
  const getValidToken = useCallback((): string | null => {
    const stored = localStorage.getItem('sharewifi_auth');
    if (!stored) return null;
    try {
      const parsed = JSON.parse(stored);
      return parsed.token || null;
    } catch {
      return null;
    }
  }, []);

  /**
   * Attempt to refresh the auth token.
   * Returns true if refresh succeeded, false if session is truly expired.
   */
  const tryRefreshToken = useCallback(async (): Promise<boolean> => {
    if (isRefreshingTokenRef.current) return false;
    isRefreshingTokenRef.current = true;
    try {
      console.log('[RealTime] Attempting token refresh before reconnect');
      await refreshToken();
      console.log('[RealTime] Token refreshed successfully');
      return true;
    } catch (error) {
      console.warn('[RealTime] Token refresh failed:', error);
      return false;
    } finally {
      isRefreshingTokenRef.current = false;
    }
  }, []);

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (!user) {
      console.log('[RealTime] No user authenticated, skipping connection');
      return;
    }

    // Prevent multiple simultaneous connections
    if (wsRef.current && (wsRef.current.readyState === WebSocket.CONNECTING || wsRef.current.readyState === WebSocket.OPEN)) {
      console.log('[RealTime] Already connected or connecting, skipping');
      return;
    }

    // Check if token still exists (may have been cleared by HTTP 401 elsewhere)
    const authToken = getValidToken();
    if (!authToken) {
      console.warn('[RealTime] No auth token available, session may have expired');
      handleSessionExpired();
      return;
    }

    // Close any existing connection first
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    wasConnectedRef.current = false;
    const wsUrl = `${getWebSocketUrl()}?token=${encodeURIComponent(authToken)}`;
    console.log('[RealTime] Connecting to WebSocket');
    setConnectionState('connecting');

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('[RealTime] WebSocket connected');
        wasConnectedRef.current = true;
        reconnectAttemptsRef.current = 0;
        setConnectionState('connected');
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data) as RealTimeEvent;
          console.log('[RealTime] Received event:', data.eventType, data);
          addEvent(data);
        } catch (error) {
          console.error('[RealTime] Failed to parse event:', error);
        }
      };

      ws.onerror = (error) => {
        console.error('[RealTime] WebSocket error:', error);
        setConnectionState('error');
      };

      ws.onclose = (event) => {
        console.log('[RealTime] WebSocket closed:', event.code, event.reason);
        wsRef.current = null;
        setConnectionState('disconnected');

        // Auth-related close codes (1008 = Policy Violation, 4001-4099 = custom auth codes)
        const isAuthCloseCode = event.code === 1008 || (event.code >= 4001 && event.code <= 4099);
        if (isAuthCloseCode) {
          console.warn('[RealTime] WebSocket closed with auth-related code:', event.code);
          handleSessionExpired();
          return;
        }

        // If the connection was never established (server rejected upgrade, likely 401),
        // attempt token refresh before retrying
        if (!wasConnectedRef.current && event.code === 1006) {
          console.warn('[RealTime] WebSocket failed to connect (code 1006), possible auth failure');

          // First check if token was already cleared by another HTTP 401
          if (!getValidToken()) {
            handleSessionExpired();
            return;
          }

          // Try refreshing the token — if it succeeds, reconnect with new token.
          // If it fails with 401, the HTTP client's handler already clears auth and redirects.
          // If it fails for another reason (e.g. network error), force session expired.
          tryRefreshToken().then((refreshed) => {
            if (refreshed && user) {
              // Token refreshed, retry connection with new token
              reconnectAttemptsRef.current = 0;
              console.log('[RealTime] Retrying connection with refreshed token');
              reconnectTimeoutRef.current = window.setTimeout(() => {
                connect();
              }, 1000);
            } else if (!getValidToken()) {
              // Token was cleared by the 401 handler, redirect should already be in progress
              // but ensure it happens
              handleSessionExpired();
            } else {
              // Refresh failed but token still exists (e.g. refresh endpoint not available)
              // Force session expired since the WS connection is failing with the current token
              handleSessionExpired();
            }
          });
          return;
        }

        // Normal reconnect for non-auth disconnections
        if (!autoReconnect || !user) return;

        if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
          console.warn('[RealTime] Max reconnect attempts reached, checking session validity');
          // Verify session is still valid after exhausting retries
          if (!getValidToken()) {
            handleSessionExpired();
          } else {
            // Token exists but WS keeps failing — try one last refresh
            tryRefreshToken().then((refreshed) => {
              if (!refreshed) {
                handleSessionExpired();
              }
            });
          }
          return;
        }

        reconnectAttemptsRef.current++;
        const delay = reconnectDelay * Math.min(reconnectAttemptsRef.current, 5); // Exponential-ish backoff, max 5x
        console.log(
          `[RealTime] Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts})`
        );
        reconnectTimeoutRef.current = window.setTimeout(() => {
          // Re-check token before each reconnect attempt
          if (!getValidToken()) {
            handleSessionExpired();
            return;
          }
          connect();
        }, delay);
      };
    } catch (error) {
      console.error('[RealTime] Failed to create WebSocket:', error);
      setConnectionState('error');
    }
  }, [user, addEvent, autoReconnect, maxReconnectAttempts, reconnectDelay, getValidToken, handleSessionExpired, tryRefreshToken]);

  // Disconnect WebSocket
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setConnectionState('disconnected');
  }, []);

  // Reconnect WebSocket
  const reconnect = useCallback(() => {
    disconnect();
    reconnectAttemptsRef.current = 0;
    connect();
  }, [connect, disconnect]);

  // Subscribe to events
  const subscribe = useCallback((callback: (event: RealTimeEvent) => void) => {
    subscribersRef.current.add(callback);
    return () => {
      subscribersRef.current.delete(callback);
    };
  }, []);

  // Clear events
  const clearEvents = useCallback(() => {
    setEvents([]);
  }, []);

  // Connect when user logs in, disconnect when user logs out
  useEffect(() => {
    if (user) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      // Only clean up timeouts, don't disconnect on unmount
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };
  }, [user]); // Only reconnect when user changes (login/logout)

  const value: RealTimeEventsContextType = {
    events,
    connectionState,
    isConnected: connectionState === 'connected',
    subscribe,
    clearEvents,
    reconnect,
  };

  return (
    <RealTimeEventsContext.Provider value={value}>
      {children}
    </RealTimeEventsContext.Provider>
  );
}

/**
 * Hook to access real-time events context
 */
export function useRealTimeEventsContext() {
  const context = useContext(RealTimeEventsContext);
  if (!context) {
    throw new Error('useRealTimeEventsContext must be used within RealTimeEventsProvider');
  }
  return context;
}
